import React, { useRef } from "react";

import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { BusinessField, BuyerAwareBuyerWayField } from "components/Form/Fields";
import { BusinessPICField } from "components/Form/Fields/PICField";

import {
  BusinessFieldFilterOptions,
  DefaultBusinessFieldFilterOptions,
} from "constants/businesses";
import { SaleType } from "constants/sale";
import { Settings } from "constants/settings";

import { getDefaultPropertyId } from "lib/properties";

import {
  getBusinesses,
  getCurrentSale,
  getCurrentSaleyardId,
  getSetting,
} from "selectors";

import { setSetting } from "actions";
import { InlineButton } from "components/Form/FormikControls";
import { QuickSellBidderNumberInput } from "components/Form/QuickSellBidderNumberInput";
import {
  useFieldSetter,
  useFieldState,
  useFormikSetDefaultPropertyOnBuyerWayChanged,
} from "hooks";

interface BuyerFieldsProps {
  isAutoFocusing: boolean;
  readOnly: boolean;
  buyerInlineButton?: InlineButton;
  isUsingRegisteredBidders: boolean;
  hideBuyerWayField?: boolean;
  hidePICField?: boolean;
}

export const BuyerFields = ({
  isAutoFocusing,
  readOnly,
  buyerInlineButton = null,
  isUsingRegisteredBidders = false,
  hideBuyerWayField = false,
  hidePICField = false,
}: BuyerFieldsProps) => {
  const setDestinationPropertyId = useFieldSetter("destinationPropertyId");
  const [buyerWay, setBuyerWay] = useFieldState("buyerWay");
  const [buyerId] = useFieldState("buyerId");

  const businessByIdLookup = useSelector(getBusinesses);
  const saleyardId = useSelector(getCurrentSaleyardId);

  function onAfterBuyerChanged(newBuyerId) {
    setBuyerWay(null);
    if (!newBuyerId) {
      setDestinationPropertyId(null);
      return;
    }
    const business = businessByIdLookup[newBuyerId] || {};
    setDestinationPropertyId(
      getDefaultPropertyId(business, saleyardId, buyerWay?.name),
    );
  }

  const onAfterBuyerWayChanged = useFormikSetDefaultPropertyOnBuyerWayChanged(
    "buyerId",
    "destinationPropertyId",
  );

  return (
    <>
      <Grid item xs={12}>
        <BusinessField
          // In bidder mode (readOnly=true) BusinessField's internal Autocomplete
          // inputValue does not track external value changes — the displayed
          // business name goes stale when bidder resolution updates buyerId via
          // setFieldValue. Keying on buyerId forces a remount on each resolution
          // so the displayed name stays correct. In editable mode a stable key
          // is used to preserve user-typed input across re-renders.
          key={readOnly ? buyerId ?? "no-buyer" : "buyer-editable"}
          name="buyerId"
          label="Buyer"
          autoFocus={isAutoFocusing}
          disabled={readOnly}
          onChangeExtra={onAfterBuyerChanged}
          inlineButton={buyerInlineButton}
          activeOnly
          searchFields={[
            ...DefaultBusinessFieldFilterOptions,
            BusinessFieldFilterOptions.properties,
          ]}
        />
      </Grid>
      {!hideBuyerWayField && (
        <Grid item xs={12}>
          <BuyerAwareBuyerWayField
            name="buyerWay"
            onChangeExtra={onAfterBuyerWayChanged}
            label={isUsingRegisteredBidders ? "Buyer Way/Account" : "Way"}
            buyerField="buyerId"
            readOnly={readOnly}
            saleyardId={isUsingRegisteredBidders ? null : saleyardId}
          />
        </Grid>
      )}
      {!hidePICField && (
        <Grid item xs={12}>
          <BusinessPICField
            buyerWayName={buyerWay?.name}
            name="destinationPropertyId"
            label="PIC"
            businessField="buyerId"
            disabled={readOnly}
            staticOptionLabel={null}
          />
        </Grid>
      )}
    </>
  );
};

interface QuickSellBidderAndBuyerSaleLotFieldsProps {
  disableInitialAutoFocus?: boolean;
}

export const QuickSellBidderAndBuyerSaleLotFields = React.memo(
  ({
    disableInitialAutoFocus = false,
  }: QuickSellBidderAndBuyerSaleLotFieldsProps) => {
    const dispatch = useDispatch();
    const hasAutoFocusedRef = useRef(false);

    const useQuickBuyerInput = useSelector(
      getSetting(Settings.useQuickBuyerInput),
    );

    const setQuickBidderInput = () =>
      dispatch(setSetting(Settings.useQuickBuyerInput, false));

    const setQuickBuyerInput = () => {
      hasAutoFocusedRef.current = true;
      dispatch(setSetting(Settings.useQuickBuyerInput, true));
    };

    const currentSaleType = useSelector(
      state => getCurrentSale(state).sale_type,
    );
    const isClearingSale = currentSaleType === SaleType.CLEARING;

    const shouldAutoFocusBidder = disableInitialAutoFocus
      ? hasAutoFocusedRef.current && !useQuickBuyerInput
      : !useQuickBuyerInput;
    const shouldAutoFocusBuyer = disableInitialAutoFocus
      ? hasAutoFocusedRef.current && useQuickBuyerInput
      : useQuickBuyerInput;

    return isClearingSale ? (
      <>
        <Grid item xs={12}>
          <QuickSellBidderNumberInput
            autoFocus={shouldAutoFocusBidder}
            inlineButton={
              !useQuickBuyerInput
                ? {
                    label: "Enter Buyer Business instead",
                    onClick: setQuickBuyerInput,
                  }
                : null
            }
            enableTemporaryBusinessCreation
            setBidderNumberFromBuyerDetails
            disabled={useQuickBuyerInput}
          />
        </Grid>
        <BuyerFields
          readOnly={!useQuickBuyerInput}
          buyerInlineButton={
            useQuickBuyerInput
              ? {
                  label: "Enter Bidder instead",
                  onClick: setQuickBidderInput,
                }
              : null
          }
          isAutoFocusing={shouldAutoFocusBuyer}
          isUsingRegisteredBidders
          hideBuyerWayField
          hidePICField
        />
      </>
    ) : (
      <>
        <BuyerFields
          readOnly={!useQuickBuyerInput}
          buyerInlineButton={
            useQuickBuyerInput
              ? {
                  label: "Enter Bidder instead",
                  onClick: setQuickBidderInput,
                }
              : null
          }
          isAutoFocusing={shouldAutoFocusBuyer}
          isUsingRegisteredBidders
        />
        <Grid item xs={12}>
          <QuickSellBidderNumberInput
            autoFocus={shouldAutoFocusBidder}
            inlineButton={
              !useQuickBuyerInput
                ? {
                    label: "Enter Buyer Business instead",
                    onClick: setQuickBuyerInput,
                  }
                : null
            }
            enableTemporaryBusinessCreation
            setBidderNumberFromBuyerDetails
            disabled={useQuickBuyerInput}
          />
        </Grid>
      </>
    );
  },
);
